import { ApprovalOperation } from 'ui-product-approvals-common';
import { useApprovalConditions } from './useApprovalConditions';
import { useApprovalContext } from './useApprovalContext';
import { useHasApprovalScope } from './useHasApprovalScope';
/**
 * Shared top-level gating for the editor approval button surfaces. Must be
 * called from a component that has already guarded an `ApprovalContext`.
 */
export const useApprovalEditorButtonsOutcome = ({
  showChildrenForRequestApproval
}) => {
  const approval = useApprovalContext();
  const hasApprovalAccess = useHasApprovalScope(approval.meta.approvalType, 'read');
  const {
    canUserTakeApprovableAction,
    isFetchComplete,
    isAwaitingApprovableObject,
    isLoading: isApprovalConfigOrDataLoading
  } = useApprovalConditions();
  const hasDataFetchError = Boolean(approval.meta.httpErrors.fetchData);

  // WARNING: changing this gating will likely break downstream ATs. Keep the
  // consumer's own button visible whenever approvals is not positively enabled
  // (in flight, disabled, or a failed config fetch where enabled resolves to
  // false) or the user lacks read access. Read access is unknown until userInfo
  // resolves (hasApprovalAccess is false while currentUser is undefined), so
  // this window renders children too — matching prod, where userInfoSync
  // resolves scopes synchronously from cache.
  if (approval.config.enabled !== true || !hasApprovalAccess) {
    return 'children';
  }

  // Awaiting a lazily-created approvable object id: nothing to act on yet, so
  // render the consumer's button rather than the skeleton (mirrors ApprovalButton).
  if (isAwaitingApprovableObject && !isApprovalConfigOrDataLoading) {
    return 'children';
  }
  if (!isFetchComplete) {
    return 'loading';
  }
  if (hasDataFetchError) {
    return 'error';
  }
  if (canUserTakeApprovableAction) {
    return 'children';
  }
  if (showChildrenForRequestApproval && approval.meta.availableOperations.includes(ApprovalOperation.REQUEST)) {
    return 'children';
  }
  return 'buttons';
};